/**
 * LocaleProvider (spec 91 — Wave 0-B).
 *
 * Owns the app's active locale and the AntD locale object fed to
 * `<ConfigProvider locale={antdLocale}>`. Switching is instant — no page reload
 * anywhere.
 *
 * en-default guarantee (spec §7 prohibition, D2): with clean localStorage the
 * initial locale is 'en' and `antdLocale` is `undefined`, so AntD keeps today's
 * exact default and dayjs stays 'en'. This is the boot state and MUST remain a
 * trivially auditable code path — there is no frontend test runner.
 *
 * AntD + dayjs locale data are loaded DYNAMICALLY (only when Spanish is chosen),
 * so English users never pay for the es bundles.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { Locale } from 'antd/es/locale';
import dayjs from 'dayjs';
import api from '../lib/api';
import vendorApi, { hasVendorSession } from '../lib/vendorApi';
import i18n, {
  LOCALE_STORAGE_KEY,
  LOCALE_EXPLICIT_KEY,
  readInitialLocale,
  type SupportedLocale,
} from './index';

interface LocaleContextValue {
  locale: SupportedLocale;
  /**
   * Switch the active locale. `explicit` (default true) marks a real user pick
   * so account adoption can never override it; the account-adoption effect calls
   * it with `explicit: false` (spec 91 §6).
   */
  setLocale: (locale: SupportedLocale, opts?: { explicit?: boolean }) => void;
  /** AntD ConfigProvider locale — `undefined` for en (keeps AntD's default). */
  antdLocale: Locale | undefined;
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

/**
 * Load the AntD + dayjs locale data for a language and apply the dayjs locale.
 * en resolves to `undefined` antd locale (AntD's built-in default) and dayjs 'en'
 * (no dynamic import needed — dayjs ships 'en' as its default).
 */
async function loadAntdLocale(
  locale: SupportedLocale,
): Promise<Locale | undefined> {
  if (locale === 'es') {
    const [{ default: esES }] = await Promise.all([
      import('antd/locale/es_ES'),
      import('dayjs/locale/es'),
    ]);
    dayjs.locale('es');
    return esES;
  }
  dayjs.locale('en');
  return undefined;
}

/**
 * Best-effort backend sync of the chosen locale. Silent on any failure — a
 * sync error must NEVER block or revert the UI switch (spec §7 prohibition).
 * Syncs only the ACTIVE portal's session (routed by the current path), so a
 * device that happens to hold both a staff and a stale vendor token doesn't
 * double-PATCH the locale onto an account the user isn't using:
 *   - On a /vendor/* route with a vendor session → PATCH /vendor/me { locale }.
 *   - Otherwise, with a staff `accessToken` → PATCH /auth/me { locale }.
 * Both endpoints accept `{ locale }` alone after Wave 0-A.
 */
function syncLocaleToBackend(locale: SupportedLocale): void {
  try {
    const onVendorPortal = window.location.pathname.startsWith('/vendor');
    if (onVendorPortal && hasVendorSession()) {
      void vendorApi.patch('/vendor/me', { locale }).catch(() => {});
    } else if (localStorage.getItem('accessToken')) {
      void api.patch('/auth/me', { locale }).catch(() => {});
    }
  } catch {
    /* localStorage access can throw in locked-down contexts — ignore. */
  }
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  // Boot state: the persisted locale (validated → 'en' when clean/unknown).
  const [locale, setLocaleState] = useState<SupportedLocale>(() =>
    readInitialLocale(),
  );
  // en → undefined so ConfigProvider keeps AntD's default (byte-identical today).
  const [antdLocale, setAntdLocale] = useState<Locale | undefined>(undefined);

  // Boot hydration: if the persisted locale is 'es', load its AntD + dayjs
  // locale data on mount so a reload restores Spanish date pickers/tables — not
  // just strings. Render proceeds immediately with the en config (undefined /
  // dayjs 'en') and swaps in once the dynamic import resolves — never blocking.
  useEffect(() => {
    if (locale === 'es') {
      let cancelled = false;
      void loadAntdLocale('es').then((loaded) => {
        if (!cancelled) setAntdLocale(loaded);
      });
      return () => {
        cancelled = true;
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLocale = (
    next: SupportedLocale,
    opts?: { explicit?: boolean },
  ) => {
    const explicit = opts?.explicit ?? true;
    setLocaleState(next);
    void i18n.changeLanguage(next);
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, next);
      // Only a real user pick marks the device as explicitly chosen; passive
      // account adoption (explicit: false) persists the value but not the flag,
      // so a later different user still adopts their own account locale.
      if (explicit) localStorage.setItem(LOCALE_EXPLICIT_KEY, '1');
    } catch {
      /* ignore persistence failures — the in-memory switch still applies */
    }
    void loadAntdLocale(next).then(setAntdLocale);
    syncLocaleToBackend(next);
  };

  return (
    <LocaleContext.Provider value={{ locale, setLocale, antdLocale }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider');
  return ctx;
}
